import { useCallback, useContext, useEffect, useRef } from "react";
import { TourContext } from "./tourContext";
import { resolveTourKeys, type TourKeyOverrides, type TourRun } from "./tourEngine";
import { isHeldByOpenDialog, scheduleFrame } from "./tourDom";

export interface UseTourOptions extends TourKeyOverrides {
  /** False while the page is still loading. Nothing starts until it is true. */
  ready: boolean;
  /** Whether the page has records the data tour can point at. */
  hasData: boolean;
}

/**
 * Starts `run` once the route has settled and a frame has passed, and no open dialog holds it.
 * Held by a dialog, it re-tries when the DOM changes, which is how a dialog closes. Returns the
 * cancel function.
 */
const startWhenUnheld = (
  run: TourRun,
  whenRouteSettled: (callback: () => void) => () => void,
  start: () => void
): (() => void) => {
  let observer: MutationObserver | null = null;
  let started = false;
  let cancelFrame: (() => void) | null = null;
  const attempt = () => {
    if (started || isHeldByOpenDialog(run)) return;
    started = true;
    observer?.disconnect();
    start();
  };

  // Coalesced onto one frame, because the check queries the page and measures anchors, and a busy
  // modal behind the tour can mutate the DOM many times in a single commit.
  const scheduleAttempt = () => {
    if (started || cancelFrame) return;
    cancelFrame = scheduleFrame(() => {
      cancelFrame = null;
      attempt();
    });
  };

  const cancelSettled = whenRouteSettled(() => {
    // A frame later, so the page has committed and painted before anything is measured.
    cancelFrame = scheduleFrame(() => {
      cancelFrame = null;
      attempt();
      if (started || typeof MutationObserver === "undefined") return;
      observer = new MutationObserver(scheduleAttempt);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        // A mounted slide panel closes by flipping inert / aria-hidden, not by leaving the DOM.
        attributeFilter: ["aria-modal", "aria-hidden", "inert"],
      });
    });
  });

  return () => {
    cancelSettled();
    cancelFrame?.();
    observer?.disconnect();
  };
};

export interface UseTourResult {
  /**
   * Replays a tour, recording nothing. With no kind: the page tour, then the data tour when the
   * page has data (what the Guided tours list runs).
   */
  startTour: (kind?: "Page" | "Data") => void;
  /** Whether this page's own page or data tour is on screen. */
  isTourActive: boolean;
}

/**
 * Starts a feature's page and data tours when they are due.
 *
 * The tours are `${featureKey}.page` and `${featureKey}.data` unless `pageTourKey` / `dataTourKey`
 * name others: a second view of the feature (`pageTourKey: "schedule.catalog", dataTourKey: null`)
 * or a flow step (`pageTourKey: "roster-import.upload-type", dataTourKey: null`).
 *
 * Auto-start holds while the page is not ready, for the frame after a navigation, and while any
 * `aria-modal` dialog is open. A replay requested from the Guided tours list for this hook's
 * feature and page tour key waits behind the same gates, runs instead of anything due, records
 * nothing, and keeps auto-start off for the rest of that visit. When the page tour ends the provider chains the data tour if the page
 * has data; if data arrives mid page tour, the data tour is queued behind it.
 *
 * A hook with `dataTourKey: null` does not register the page with the provider. The registration
 * is keyed by feature and only carries `hasData` for the feature's data tour, so a second view
 * registering would overwrite, then on unmount delete, the feature page's own.
 *
 * Inert outside `TourProvider`.
 */
export const useTour = (
  featureKey: string,
  { ready, hasData, pageTourKey, dataTourKey }: UseTourOptions
): UseTourResult => {
  const context = useContext(TourContext);
  const updatePage = context?.updatePage;
  const unregisterPage = context?.unregisterPage;
  const status = context?.status;
  const whenRouteSettled = context?.whenRouteSettled;
  const activeTour = context?.activeTour ?? null;
  const getDueRun = context?.getDueRun;
  const startRun = context?.startRun;
  const queueRun = context?.queueRun;
  const pendingReplay = context?.pendingReplay ?? null;
  const getReplayRun = context?.getReplayRun;
  const clearReplay = context?.clearReplay;
  const pathname = context?.pathname;

  const { page: pageKey, data: dataKey } = resolveTourKeys(featureKey, { pageTourKey, dataTourKey });
  const registers = dataKey !== null;
  // A request for this page's page tour, or for its data tour on its own (the Players list's
  // "Open a player" entry), which this hook runs too rather than a second hook on the page.
  const replayRequest =
    pendingReplay &&
    pendingReplay.featureKey === featureKey &&
    (pendingReplay.pageKey === pageKey || (dataKey !== null && pendingReplay.pageKey === dataKey))
      ? pendingReplay
      : null;

  useEffect(() => {
    if (registers) updatePage?.(featureKey, { ready, hasData });
  }, [registers, updatePage, featureKey, ready, hasData]);

  useEffect(
    () => () => {
      if (registers) unregisterPage?.(featureKey);
    },
    [registers, unregisterPage, featureKey]
  );

  /**
   * Set once this visit has run a requested replay, so auto-start stays out of the rest of it: a
   * coach who skips the replay has just said no to this tour, and a still-due copy starting straight
   * after would ask again. A fresh mount starts false, and any pathname change resets it, so the
   * next visit auto-starts as normal. Declared before the effects that read it, so on a pathname
   * change the reset runs first.
   */
  const replayedThisVisitRef = useRef(false);
  useEffect(() => {
    replayedThisVisitRef.current = false;
  }, [pathname]);

  // A replay asked for from the Guided tours list. Same gates as auto-start, which it pre-empts.
  useEffect(() => {
    if (!replayRequest || !getReplayRun || !clearReplay || !startRun || !whenRouteSettled) return;
    if (status !== "ready" || !ready || activeTour) return;
    const found =
      replayRequest.pageKey === pageKey
        ? getReplayRun(featureKey, { pageTourKey, dataTourKey })
        : getReplayRun(featureKey, { pageTourKey: replayRequest.pageKey, dataTourKey: null });
    const run = found && replayRequest.standalone ? { ...found, standalone: true } : found;
    if (!run) {
      // Nothing this user can see: drop the request rather than leave it waiting.
      clearReplay(replayRequest);
      return;
    }
    return startWhenUnheld(run, whenRouteSettled, () => {
      clearReplay(replayRequest);
      replayedThisVisitRef.current = true;
      startRun(run);
    });
  }, [
    replayRequest,
    pageKey,
    getReplayRun,
    clearReplay,
    startRun,
    whenRouteSettled,
    status,
    ready,
    activeTour,
    featureKey,
    pageTourKey,
    dataTourKey,
  ]);

  // Auto-start. Stands aside while a replay for this page is waiting, so the replay runs first, and
  // for the rest of a visit that has run one.
  const replayWaiting = replayRequest !== null;
  useEffect(() => {
    if (status !== "ready" || !ready || activeTour || !getDueRun || !startRun || !whenRouteSettled) {
      return;
    }
    if (replayWaiting || replayedThisVisitRef.current) return;
    const candidate: TourRun | null =
      getDueRun(pageKey) ?? (hasData && dataKey !== null ? getDueRun(dataKey) : null);
    if (!candidate) return;
    return startWhenUnheld(candidate, whenRouteSettled, () => startRun(candidate));
  }, [
    status,
    ready,
    activeTour,
    getDueRun,
    startRun,
    whenRouteSettled,
    pageKey,
    dataKey,
    hasData,
    replayWaiting,
    // Not read, but a pathname change clears `replayedThisVisitRef`, and auto-start has to look again.
    pathname,
  ]);

  // Data arrived during this page's own page tour: queue the data tour behind it. Matched by key,
  // so a different Page tour of the same feature (schedule's catalog) never queues this data tour.
  const activeRun = activeTour?.run;
  useEffect(() => {
    if (!hasData || dataKey === null || !activeRun || !getDueRun || !queueRun || activeRun.replay) {
      return;
    }
    if (activeRun.tour.key !== pageKey) return;
    const dataRun = getDueRun(dataKey);
    if (dataRun) queueRun(dataRun);
  }, [hasData, activeRun, getDueRun, queueRun, pageKey, dataKey]);

  const startTour = useCallback(
    (kind?: "Page" | "Data") => {
      if (!context) return;
      if (!kind) {
        context.replayFeature(featureKey, { pageTourKey, dataTourKey });
        return;
      }
      const key = kind === "Page" ? pageKey : dataKey;
      if (key !== null) context.startTour(key, { replay: true });
    },
    [context, featureKey, pageTourKey, dataTourKey, pageKey, dataKey]
  );

  return {
    startTour,
    isTourActive: activeRun !== undefined && (activeRun.tour.key === pageKey || activeRun.tour.key === dataKey),
  };
};
